import type { ComponentType } from 'react';
import {
  type IconProps as PhosphorIconProps,
  ArrowLineRight,
  ArrowsDownUp,
  Bell,
  CaretDown,
  CaretLeft,
  CaretRight,
  Check,
  CheckCircle,
  DotsSixVertical,
  DownloadSimple,
  Info,
  Eye,
  EyeSlash,
  FileDoc,
  Files,
  FileText,
  FileXls,
  Gear,
  List,
  MagnifyingGlass,
  Minus,
  PencilSimple,
  Plus,
  Power,
  SignOut,
  Sparkle,
  Spinner,
  SquaresFour,
  ThumbsDown,
  ThumbsUp,
  Trash,
  UploadSimple,
  UsersThree,
  X,
  XCircle,
} from '@phosphor-icons/react';

/**
 * Phosphor line icons (https://phosphoricons.com) at the `regular` weight,
 * matching the design system in Figma. Colour is inherited from the parent via
 * Phosphor's default `currentColor`, so callers set it with Tailwind text
 * utilities (e.g. `text-text-primary`, `text-brand-primary`).
 *
 * The stable `<Icon name="…" />` wrapper keeps every call site decoupled from
 * the underlying library — each name maps to one Phosphor glyph below.
 */
const ICONS = {
  dashboard: SquaresFour,
  fileText: FileText,
  team: UsersThree,
  settings: Gear,
  signOut: SignOut,
  power: Power,
  search: MagnifyingGlass,
  bell: Bell,
  chevronLeft: CaretLeft,
  chevronRight: CaretRight,
  chevronDown: CaretDown,
  plus: Plus,
  download: DownloadSimple,
  grid: SquaresFour,
  list: List,
  close: X,
  upload: UploadSimple,
  check: Check,
  minus: Minus,
  gripVertical: DotsSixVertical,
  docFile: FileDoc,
  xlsxFile: FileXls,
  filesStack: Files,
  pencil: PencilSimple,
  trash: Trash,
  sparkle: Sparkle,
  thumbsUp: ThumbsUp,
  thumbsDown: ThumbsDown,
  eye: Eye,
  eyeSlash: EyeSlash,
  arrowsDownUp: ArrowsDownUp,
  arrowLineRight: ArrowLineRight,
  spinner: Spinner,
  checkCircle: CheckCircle,
  xCircle: XCircle,
  info: Info,
} satisfies Record<string, ComponentType<PhosphorIconProps>>;

export type IconName = keyof typeof ICONS;

export type IconProps = {
  name: IconName;
  size?: number | string;
  /**
   * Retained for API compatibility with the previous (stroke-based) icon set.
   * Phosphor uses a `weight` rather than a stroke width, so this is a no-op —
   * all glyphs render at the `regular` weight.
   */
  strokeWidth?: number;
} & Omit<PhosphorIconProps, 'name'>;

export function Icon({ name, size = 16, strokeWidth: _strokeWidth, ...rest }: IconProps) {
  const Glyph = ICONS[name];
  return <Glyph size={size} weight="regular" data-icon={name} aria-hidden="true" {...rest} />;
}
